import { HttpApiService } from "@/shared/services/httpApi.service";
import type { NewConfirmationFormData } from "../types/new-confirmation.types";
import type { PaginationParams } from "@/shared/types/pagination.types";
import type {
  ConfirmationsListResponse,
  ConfirmationHistoryListResponse,
  ConfirmationByIdPayload,
} from "../types/confirmations-api.types";
import { buildFormDataForConfirmation } from "../utils/formDataHelpers";
import { CONFIRMATIONS_API_ENDPOINTS } from "../constants/confirmations.constants";

export interface CreateConfirmationResponse {
  id?: string;
  [key: string]: unknown;
}

export type FetchConfirmationsParams = PaginationParams;
export type FetchHistoryParams = PaginationParams;

export const confirmationsService = {
  create: async (data: NewConfirmationFormData): Promise<CreateConfirmationResponse> => {
    const formData = buildFormDataForConfirmation(data);
    const response = await HttpApiService.post<
      CreateConfirmationResponse,
      FormData
    >(CONFIRMATIONS_API_ENDPOINTS.CONFIRMATIONS, formData);
    return response.data;
  },

  fetchById: async (id: string): Promise<ConfirmationByIdPayload> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.confirmationById(id);
    const response = await HttpApiService.get<ConfirmationByIdPayload>(path);
    return response.data;
  },

  fetchList: async (
    params?: FetchConfirmationsParams
  ): Promise<ConfirmationsListResponse> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.CONFIRMATIONS;
    const queryParams: string[] = [];
    if (params?.skip != null) queryParams.push(`skip=${params.skip}`);
    if (params?.limit != null) queryParams.push(`limit=${params.limit}`);
    const url = queryParams.length ? `${path}?${queryParams.join("&")}` : path;
    const response = await HttpApiService.get<ConfirmationsListResponse>(url);
    return response.data;
  },

  getAttachmentPresignedUrl: async (confirmationId: string): Promise<string> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.attachmentPresignedUrl(confirmationId);
    const response = await HttpApiService.get<{ url: string }>(path);
    return response.data.url;
  },

  fetchHistory: async (
    confirmationId: string,
    params?: FetchHistoryParams
  ): Promise<ConfirmationHistoryListResponse> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.history(confirmationId);
    const queryParams: string[] = [];
    if (params?.skip != null) queryParams.push(`skip=${params.skip}`);
    if (params?.limit != null) queryParams.push(`limit=${params.limit}`);
    const url = queryParams.length ? `${path}?${queryParams.join("&")}` : path;
    const response = await HttpApiService.get<ConfirmationHistoryListResponse>(url);
    return response.data;
  },

  cancel: async (confirmationId: string, description: string): Promise<void> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.status(confirmationId);
    await HttpApiService.patch<unknown, { status: string; description: string }>(path, {
      status: "CANCELLED",
      description: description.trim(),
    });
  },

  take: async (confirmationId: string): Promise<void> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.take(confirmationId);
    await HttpApiService.post<unknown, Record<string, never>>(path, {});
  },

  updateConfirmation: async (
    confirmationId: string,
    payload: { confirmation_source: string }
  ): Promise<void> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.confirmationById(confirmationId);
    await HttpApiService.patch<unknown, { confirmation_source: string }>(path, payload);
  },

  uploadAttachment: async (
    confirmationId: string,
    files: File[]
  ): Promise<void> => {
    if (files.length === 0) return;
    const path = CONFIRMATIONS_API_ENDPOINTS.attachment(confirmationId);
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append("attachment", files[i], files[i].name);
      await HttpApiService.post<unknown, FormData>(path, formData);
    }
  },

  confirm: async (confirmationId: string): Promise<void> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.status(confirmationId);
    await HttpApiService.patch<unknown, { status: string }>(path, {
      status: "CONFIRMED",
    });
  },

  notifyClient: async (confirmationId: string): Promise<void> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.status(confirmationId);
    await HttpApiService.patch<unknown, { status: string }>(path, {
      status: "NOTIFIED",
    });
  },

  sendTakerReminder: async (confirmationId: string): Promise<void> => {
    const path = CONFIRMATIONS_API_ENDPOINTS.takerReminder(confirmationId);
    await HttpApiService.post<unknown, Record<string, never>>(path, {});
  },
};
